import { AlertTriangle } from "lucide-react";
import React, { Component, ReactNode } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error boundary specifically for Popover components to prevent infinite loops
 * from crashing the entire application
 */
export class PopoverErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error("PopoverErrorBoundary caught an error:", error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // In development, you might want to report this to an error reporting service
    if (process.env.NODE_ENV === "development") {
      console.error("Error:", error);
      console.error("Component Stack:", errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Alert variant="destructive" className="m-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Component Error</AlertTitle>
          <AlertDescription>
            A component error occurred. Please try refreshing the page.
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm">Technical Details</summary>
                <pre className="mt-1 text-xs overflow-auto">{this.state.error.toString()}</pre>
              </details>
            )}
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap any component with PopoverErrorBoundary
 */
export function withPopoverErrorBoundary<T extends Record<string, unknown>>(
  Component: React.ComponentType<T>,
  fallback?: ReactNode
) {
  const WrappedComponent = (props: T) => (
    <PopoverErrorBoundary fallback={fallback}>
      <Component {...props} />
    </PopoverErrorBoundary>
  );

  WrappedComponent.displayName = `withPopoverErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}
